import {
  DeleteConfirm,
  IconButton,
  ReferenceArea,
  ReferenceField,
  SectionFooter,
} from '@/components/admin/reglages/reference-list';
import { Callout } from '@/components/ui/callout';
import type { ToneRow } from '@/lib/persona-settings';
import { newToneRow, toneBlocked, toneDirty, toneRowsOf } from '@/lib/persona-settings';
import type { PersonaToneEntry } from '@matter_extractor/shared';
import { PERSONA_TONE_NAME_MAX, PERSONA_TONE_TEXT_MAX } from '@matter_extractor/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useId, useState } from 'react';

/**
 * Section 2 of Réglages > Personas: the tone library (§11.5, planche
 * `ReglagesPersonas`).
 *
 * The tones a persona's editor offers as a cloud of tags. Not versioned, and
 * THE PERSONAS KEEP THEIR COPY: renaming a tone, rewriting its text or deleting
 * it changes no persona, published or not (§10.1). The screen says that in the
 * deletion confirmation, because it is the one thing an operator would
 * otherwise hesitate over.
 *
 * ## No order, and no minimum
 *
 * The two differences from the scale beside it, and both come from what a tone
 * IS: a cloud of tags has no first element, so nothing here moves up or down;
 * and an empty library is legal, so nothing refuses the last deletion.
 *
 * Everything the two sections DO share - the fields, the trash, the inline
 * confirmation, the block footer - is in `reference-list.tsx` rather than
 * copied: the two copies had already drifted on the width of the name field
 * inside one lot.
 *
 * ## Why « Ajouter un ton » is here at all
 *
 * §11.5 sends the CREATION of a tone to the persona editor's modal - « on crée
 * un ton au moment où on en a besoin, on l'entretient ici » - and the planche
 * draws the dashed button anyway. Both are kept, and the note under the list
 * says which is the usual road: until issue #42 lands that modal, this button
 * is the only way a tone can enter the library at all, and a library nothing
 * can fill is a section that cannot be used or photographed.
 */

export interface ToneLibraryProps {
  readonly tones: readonly PersonaToneEntry[];
  readonly saving: boolean;
  readonly deletingId: string | null;
  readonly error: string | null;
  readonly onSave: (rows: readonly ToneRow[]) => void;
  readonly onDelete: (id: string) => void;
}

export function ToneLibrary({
  tones,
  saving,
  deletingId,
  error,
  onSave,
  onDelete,
}: ToneLibraryProps) {
  const [rows, setRows] = useState<ToneRow[]>(() => toneRowsOf(tones));
  const [added, setAdded] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);
  const headingId = useId();

  const blocked = toneBlocked(rows);
  const dirty = toneDirty(rows, tones);

  function patch(key: string, over: Partial<ToneRow>): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...over } : row)));
  }

  return (
    <section aria-labelledby={headingId} data-testid="reglages-tons" className="space-y-4">
      <header className="space-y-1">
        <h3 id={headingId} className="font-semibold text-bleu-nuit text-lg leading-7">
          Bibliothèque de tons
        </h3>
        <p className="max-w-3xl text-gray-700 text-sm">
          Les tons que l’éditeur d’un persona propose en nuage de tags. Elle n’est pas versionnée,
          et les personas gardent leur copie : modifier ou supprimer un ton ici ne change aucun
          persona, publié ou non.
        </p>
      </header>

      {rows.length === 0 ? (
        <p data-testid="reglages-tons-vide" className="text-gray-600 text-sm">
          La bibliothèque est vide. Un ton ajouté ici est proposé dans l’éditeur de chaque persona.
        </p>
      ) : null}

      <ul className="space-y-3">
        {rows.map((row) => {
          /* Narrowed once, above the JSX - see `involvement-scale.tsx`. */
          const savedId = row.id;
          return (
            <li key={row.key} data-testid="reglages-ton" className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 space-y-2">
                  <ReferenceField
                    label="Nom du ton"
                    testId="reglages-ton-nom"
                    value={row.name}
                    max={PERSONA_TONE_NAME_MAX}
                    placeholder="chaleureux"
                    // The one écart the maquette assumes: a full-width field for
                    // « chaleureux » lies about what is expected.
                    narrow
                    onChange={(next) => patch(row.key, { name: next })}
                  />
                  <ReferenceArea
                    label="Texte du ton"
                    testId="reglages-ton-texte"
                    value={row.text}
                    max={PERSONA_TONE_TEXT_MAX}
                    placeholder="Comment parler : tutoiement, longueur des phrases, ce qui est permis."
                    onChange={(next) => patch(row.key, { text: next })}
                  />
                </div>

                <div className="shrink-0">
                  <IconButton
                    label={`Supprimer le ton ${row.name || 'sans nom'}`}
                    tone="danger"
                    testId="reglages-ton-supprimer"
                    onClick={() => {
                      // A row that was never saved has nothing to delete on the
                      // server: it just leaves the block.
                      if (savedId === null) {
                        setRows((current) => current.filter((one) => one.key !== row.key));
                        return;
                      }
                      setConfirming(row.key);
                    }}
                  >
                    <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                  </IconButton>
                </div>
              </div>

              {confirming === row.key && savedId !== null ? (
                <DeleteConfirm
                  testId="reglages-ton-confirmation"
                  label={`Confirmer la suppression du ton « ${row.name} »`}
                  busy={deletingId === savedId}
                  onCancel={() => setConfirming(null)}
                  onConfirm={() => {
                    onDelete(savedId);
                    setConfirming(null);
                  }}
                >
                  Supprimer « {row.name} » ? La suppression est <strong>dure</strong> : le ton
                  quitte la bibliothèque et aucune provenance n’est gardée. Les personas qui portent
                  ce ton <strong>gardent leur copie</strong>, publiée ou non, et ne bougent pas.
                  {dirty
                    ? ' Elle relit la bibliothèque : les modifications de ce bloc qui ne sont pas enregistrées seront perdues.'
                    : ''}
                </DeleteConfirm>
              ) : null}
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        data-testid="reglages-ton-ajouter"
        onClick={() => {
          setRows((current) => [...current, newToneRow(added)]);
          setAdded((count) => count + 1);
        }}
        className="flex h-13 w-full cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white font-semibold text-base text-bleu-nuit hover:bg-gray-50"
      >
        <Plus size={18} strokeWidth={2} aria-hidden="true" />
        Ajouter un ton
      </button>

      <Callout tone="neutral">
        <p>
          Le nom d’un ton est unique dans la bibliothèque : un nom déjà pris est refusé en nommant
          l’autre ton. La bibliothèque n’est pas versionnée : l’enregistrement écrase, et il n’y a
          pas d’historique à remonter.
        </p>
      </Callout>

      <SectionFooter
        testId="reglages-tons-enregistrer"
        label="Enregistrer la bibliothèque"
        blocked={blocked}
        dirty={dirty}
        saving={saving}
        error={error}
        onSave={() => onSave(rows)}
      />
    </section>
  );
}
